import * as React from 'react'

import { observer } from 'mobx-react'

import { SitePermitStatus } from '~/client/graph'
import SearchBar from '~/client/src/shared/components/SearchBar/SearchBar'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import { WorkflowStepCategory } from '~/client/src/shared/enums/WorkflowStepCategory'
import {
  getFormStatusDisplayName,
  getWorkflowCategoryDisplayName,
} from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IStatusItem } from '~/client/src/shared/utils/permitStatusUtils'

import LogisticsListStore from './LogisticsList/LogisticsList.store'

interface IProps {
  logisticsListStore: LogisticsListStore
  onStatusSelected: (item: IStatusItem) => void
  onClose: () => void
}

const StatusUpdateMenu: React.FC<IProps> = ({
  logisticsListStore,
  onStatusSelected,
  onClose,
}: IProps) => {
  const [query, setQuery] = React.useState('')

  const statusCategoryMap = {
    [WorkflowStepCategory.INITIATION]: [
      SitePermitStatus.Requested,
      SitePermitStatus.Submitted,
    ],
    [WorkflowStepCategory.APPROVAL]: [
      SitePermitStatus.Reviewed,
      SitePermitStatus.Accepted,
      SitePermitStatus.Denied,
    ],
    [WorkflowStepCategory.START]: [
      SitePermitStatus.Active,
      SitePermitStatus.OnSite,
    ],
    [WorkflowStepCategory.INSPECTION]: [
      SitePermitStatus.ToInspect,
      SitePermitStatus.Passed,
      SitePermitStatus.Failed,
    ],
    [WorkflowStepCategory.FINISH]: [SitePermitStatus.Done],
  }

  const permits = logisticsListStore.getSelectedNonDraftPermits
  if (!permits.length) return null

  const commonItems = logisticsListStore.getCommonStepItems(permits)

  const filteredItems = commonItems.filter(item =>
    getFormStatusDisplayName(item.status, item.workflowStepLevel)
      ?.toLowerCase()
      .includes(query.trim().toLowerCase()),
  )

  return (
    <div className="col no-outline-container mw400 tw:px-2.5 tw:pb-2.5">
      <div className="col x-between y-center">
        <SearchBar
          value={query}
          onChange={setQuery}
          onReset={() => setQuery('')}
          onTagClick={() => setQuery('')}
          customPlaceholder={Localization.translator.search}
          useAlternativeLayout
          className="no-grow"
        />
        <div className="text large grey-30 tw:pt-2.5 tw:pb-[5px] tw:pl-2.5">{`${filteredItems.length} ${Localization.translator.statuses}`}</div>
      </div>

      <div className="overflow-auto">
        {!filteredItems.length && (
          <div className="text center tw:p-[15px]">
            {Localization.translator.noResults}
          </div>
        )}

        {filteredItems.length > 0 &&
          filteredItems.map((item, index) => {
            const getStatusCategory = (
              s: SitePermitStatus,
            ): WorkflowStepCategory | null => {
              for (const category of Object.values(WorkflowStepCategory)) {
                const statuses: SitePermitStatus[] = statusCategoryMap[category]
                if (statuses?.includes(s)) return category
              }
              return null
            }

            const showDivider =
              getStatusCategory(filteredItems[index - 1]?.status) !==
              getStatusCategory(item.status)

            return (
              <React.Fragment key={`${item.status}:${item.workflowStepLevel}`}>
                {showDivider && (
                  <div className="row section-separator tw:px-6 overflow-hidden">
                    <hr className="ba-none bt-light-input-border min-width30" />
                    <span className="text large light no-white-space-wrap lpMinus01 line-extra-large w-fit-content tw:mx-2.5 ellipsis flex-grow3">
                      {getWorkflowCategoryDisplayName(
                        getStatusCategory(item.status),
                      )}
                    </span>
                    <hr className="ba-none bt-light-input-border min-width30" />
                  </div>
                )}
                <div
                  className="tw:py-2 tw:pl-2.5 highlighted-hover pointer"
                  onClick={() => {
                    onClose()
                    onStatusSelected(item)
                  }}
                >
                  <StatusBadge
                    status={item.status}
                    isLate={false}
                    workflowStepLevel={item.workflowStepLevel}
                  />
                </div>
              </React.Fragment>
            )
          })}
      </div>
    </div>
  )
}

export default observer(StatusUpdateMenu)
